import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Calendar, Clock, MapPin, Play, Flame, Sparkles } from "lucide-react";
import api from "@/lib/api";
import FirstVisit from "@/components/FirstVisit";

const HERO_IMG_FALLBACK = "fallback";

const DEFAULT_HERO_IMG =
  "https://images.unsplash.com/photo-1522158637959-30385a09e0da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHx3b3JzaGlwJTIwY29uY2VydCUyMHlvdXRoJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3ODIwMDU5MTF8MA&ixlib=rb-4.1.0&q=85";

const DUBAI_IMG =
  "https://images.unsplash.com/photo-1612873649383-edf91f1cf7fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxkdWJhaSUyMHNreWxpbmUlMjBzdW5zZXR8ZW58MHx8fHwxNzgyMDA1OTExfDA&ixlib=rb-4.1.0&q=85";

const COMMUNITY_IMG =
  "https://images.pexels.com/photos/6141087/pexels-photo-6141087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const GROUPS_IMG =
  "https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxjaHVyY2glMjB5b3V0aCUyMGdyb3VwJTIwZ2F0aGVyaW5nfGVufDB8fHx8MTc4MjAwNTkxMXww&ixlib=rb-4.1.0&q=85";

const SERVICES_FALLBACK = [
  { day: "Sunday · 1st", name: "Sunday Service", time: "9:00 AM", note: "Worship · Word · Fellowship" },
  { day: "Sunday", name: "Sunday School", time: "10:30 – 11:15 AM", note: "Same Word · Multiple classes" },
  { day: "Sunday · 2nd", name: "Sunday Service", time: "11:15 AM", note: "Worship · Word · Spirit" },
  { day: "Wednesday", name: "Word of Life", time: "7:00 PM", note: "Bible Study · 1st Wed: Communion" },
  { day: "Saturday", name: "Evangelism Outreach", time: "4:00 PM", note: "Souls · Streets · Sharing Christ" },
  { day: "Daily", name: "Open Heavens", time: "5:45 AM", note: "15-min devotional · Instagram Live" },
];

const MINISTRIES = [];

function MinistryIcon({ name, className }) {
  const I = LucideIcons[name] || LucideIcons.Flame;
  return <I className={className} />;
}

function youtubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:v=|youtu\.be\/|\/embed\/)([\w-]{6,})/);
  return match ? match[1] : null;
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [ytLatest, setYtLatest] = useState(null);
  const [content, setContent] = useState({});
  const [ministries, setMinistries] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [live, setLive] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/sermons").then((r) => setSermons(r.data)).catch(() => {});
    api.get("/youtube/videos?limit=1").then((r) => setYtLatest(r.data?.[0] || null)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data || {})).catch(() => {});
    api.get("/ministries").then((r) => setMinistries(r.data || [])).catch(() => {});
    api.get("/testimonies/featured?limit=2").then((r) => setTestimonies(r.data || [])).catch(() => {});
    api.get("/youtube/live-status").then((r) => setLive(r.data?.is_live ? r.data : null)).catch(() => {});
  }, []);

  const hero = content.home_hero || {};
  const who = content.home_who_we_are || {};
  const servicesBlock = content.home_services || {};
  const services = Array.isArray(servicesBlock.items) && servicesBlock.items.length > 0
    ? servicesBlock.items
    : SERVICES_FALLBACK;
  const fellowshipBlock = content.home_fellowships || {};
  const fellowships = Array.isArray(fellowshipBlock.items) ? fellowshipBlock.items : [];
  const heroImg = hero.image_url || DEFAULT_HERO_IMG;
  const verse = content.daily_verse || {};
  const milestone = content.home_milestone || {};

  // Prefer the live YouTube feed; fall back to the most-recent manually curated video sermon
  const fallback = sermons.find((s) => (s.sermon_type || "video") === "video" && s.youtube_url);
  const featuredSermon = ytLatest
    ? {
        title: ytLatest.title,
        speaker: ytLatest.speaker,
        date: (ytLatest.published || "").slice(0, 10),
        embed: ytLatest.embed_url,
        description: ytLatest.description,
        source: "youtube",
      }
    : fallback
    ? {
        title: fallback.title,
        speaker: fallback.speaker,
        date: fallback.date,
        embed: youtubeId(fallback.youtube_url)
          ? `https://www.youtube.com/embed/${youtubeId(fallback.youtube_url)}`
          : null,
        description: fallback.description,
        source: "curated",
      }
    : null;

  return (
    <div data-testid="home-page" className="bg-[#050505]">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-end overflow-hidden grain"
      >
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Worship"
            className="w-full h-full object-cover opacity-70"
            style={{
              objectPosition: hero.image_url_position || "center",
              transform: `scale(${hero.image_url_scale || 1})`,
              transformOrigin: hero.image_url_position || "center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl space-y-6"
          >
            <div className="flex items-center gap-3">
              <div className="bg-white/95 rounded-sm p-1 hidden sm:block">
                <img
                  src="/rccg-logo.png"
                  alt="RCCG"
                  data-testid="rccg-logo-hero"
                  className="w-7 h-7 object-contain"
                />
              </div>
              <span className="h-px w-12 bg-[#ff5c00]" />
              <span className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00]">
                {hero.eyebrow || "RCCG · UGYC · Dubai"}
              </span>
            </div>
            <h1
              data-testid="hero-heading"
              className="font-display text-5xl sm:text-7xl lg:text-8xl xl:text-9xl uppercase font-bold leading-[0.9] tracking-tighter text-balance"
            >
              {hero.headline_lead || "The"} <span className="text-[#ff5c00]">{hero.headline_accent || "Unstoppable"}</span><br />
              {hero.headline_tail || "Generation"}
            </h1>
            <p className="text-zinc-300 text-lg md:text-xl max-w-2xl leading-relaxed">
              {hero.body || "A Spirit-filled Pentecostal church in Dubai — a parish of RCCG. We are a generation set to dominate the seven mountains of societal influence for God's glory. You are welcome here."}
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-4">
              {live?.is_live && (
                <a
                  href={live.watch_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-live-now"
                  className="inline-flex items-center gap-2 bg-red-500/15 border border-red-500/50 hover:bg-red-500/25 text-red-200 font-mono-accent text-[11px] tracking-[0.2em] uppercase px-4 py-2 rounded-full transition-colors"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                  </span>
                  We&apos;re Live Now — Tap to Watch
                </a>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Link
                to="/#plan-visit"
                data-testid="hero-cta-visit"
                className="group inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(255,92,0,0.7)]"
              >
                Plan a Visit
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://youtube.com/@rccg_ugycdubai"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-watch"
                className="group inline-flex items-center gap-2 border border-white/20 hover:border-[#ff5c00] hover:text-[#ff5c00] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all"
              >
                <Play className="w-4 h-4" />
                Watch Live
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 inset-x-0 border-t border-white/10 bg-black/40 backdrop-blur-md py-3 z-10">
          <Marquee gradient={false} speed={40}>
            {Array.from({ length: 10 }).map((_, i) => (
              <span
                key={i}
                className="font-mono-accent text-xs tracking-[0.3em] uppercase text-zinc-500 mx-8 flex items-center gap-6"
              >
                Sundays · 9AM & 11AM
                <Sparkles className="w-3 h-3 text-[#ff5c00]" />
                With God All Things Are Possible · Matthew 19:26
                <Sparkles className="w-3 h-3 text-[#ff5c00]" />
                RCCG UGYC Dubai
                <Sparkles className="w-3 h-3 text-[#ff5c00]" />
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* DAILY VERSE BANNER */}
      {verse.enabled !== false && (verse.text || verse.reference) && (
        <section data-testid="daily-verse-section" className="border-b border-white/5 bg-[#080808]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-7 flex flex-col md:flex-row md:items-center gap-3 md:gap-8">
            <p
              data-testid="daily-verse-eyebrow"
              className="font-mono-accent text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#ff5c00] shrink-0 flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" /> {verse.eyebrow || "Today's Word"}
            </p>
            <blockquote className="flex-1 text-zinc-200 text-base md:text-lg italic leading-snug">
              <span data-testid="daily-verse-text">&ldquo;{verse.text}&rdquo;</span>
              {verse.reference && (
                <span data-testid="daily-verse-reference" className="font-mono-accent not-italic text-[10px] md:text-xs tracking-[0.2em] uppercase text-zinc-500 ml-3 whitespace-nowrap">
                  — {verse.reference}
                </span>
              )}
            </blockquote>
          </div>
        </section>
      )}

      {/* SERVICE TIMES — Bento */}
      <section data-testid="service-times-section" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">
                {servicesBlock.eyebrow || "When We Gather"}
              </p>
              <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter">
                {servicesBlock.heading || "Service Times"}
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="services-directions-link"
              className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#ff5c00] inline-flex items-center gap-2"
            >
              Get Directions <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, idx) => (
              <motion.div
                key={`${s.name}-${s.time}-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`service-card-${idx}`}
                className="group relative bg-[#0f0f14] border border-white/10 p-8 rounded-sm overflow-hidden hover:border-[#ff5c00]/60 transition-all hover:-translate-y-1"
              >
                <div className="absolute -right-12 -top-12 w-40 h-40 bg-[#ff5c00]/10 blur-3xl group-hover:bg-[#ff5c00]/20 transition-colors" />
                <Flame className="w-7 h-7 text-[#ff5c00] mb-6" />
                <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2">{s.day}</p>
                <h3 className="font-display text-2xl uppercase mb-3 tracking-tight">{s.name}</h3>
                <div className="flex items-center gap-2 text-zinc-300 text-sm mb-1">
                  <Clock className="w-4 h-4 text-[#ff5c00]" /> {s.time}
                </div>
                <p className="text-zinc-500 text-sm mt-3">{s.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MILESTONE — Years of Ministry */}
      {(milestone.value || milestone.label) && (
        <section data-testid="home-milestone-section" className="py-16 md:py-20 px-6 md:px-12 bg-[#050505] border-t border-white/5">
          <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5">
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">{milestone.eyebrow || "By His Grace"}</p>
              <div className="flex items-baseline gap-4">
                <span data-testid="milestone-value" className="font-display text-7xl md:text-8xl lg:text-9xl text-[#ff5c00] leading-[0.8]">{milestone.value || "13+"}</span>
                <span data-testid="milestone-label" className="font-display uppercase tracking-tight text-2xl md:text-3xl text-white leading-tight">{milestone.label || "Years of Ministry"}</span>
              </div>
            </div>
            {milestone.body && (
              <p data-testid="milestone-body" className="md:col-span-7 text-zinc-300 text-base md:text-lg leading-relaxed border-l border-[#ff5c00]/40 md:pl-8">
                {milestone.body}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ABOUT EDITORIAL */}
      <section data-testid="about-preview-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00]">{who.eyebrow || "Who We Are"}</p>
            <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter leading-[0.95]">
              {who.heading_lead || "A redeemed family where"} <span className="text-gradient-flame">{who.heading_accent || "faith"}</span> {who.heading_tail || "grows."}
            </h2>
            <p className="text-zinc-300 text-lg leading-relaxed">
              {who.body || "We are a Christ-centered family — a parish of The Redeemed Christian Church of God in Dubai — committed to holiness, prayer, worship, and the teaching of God's Word. Our mission is to raise people who love God, grow in faith, and impact their world through the power of the Holy Spirit."}
            </p>
            <div className="grid grid-cols-3 gap-6 pt-4 border-t border-white/10">
              <div>
                <div className="font-display text-3xl text-[#ff5c00]">{who.stat1_value || "3"}</div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{who.stat1_label || "Weekly Gatherings"}</p>
              </div>
              <div>
                <div className="font-display text-3xl text-[#ff5c00]">{who.stat2_value || "7"}</div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{who.stat2_label || "Mountains of Influence"}</p>
              </div>
              <div>
                <div className="font-display text-3xl text-[#ff5c00]">{who.stat3_value || "∞"}</div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{who.stat3_label || "Family"}</p>
              </div>
            </div>
            <Link
              to="/about"
              data-testid="about-preview-link"
              className="inline-flex items-center gap-2 font-mono-accent text-xs tracking-[0.2em] uppercase text-white hover:text-[#ff5c00]"
            >
              Read our story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4 relative">
            <div className="rounded-sm overflow-hidden h-72 w-full">
              <img
                src={who.image1 || COMMUNITY_IMG}
                alt=""
                className="object-cover h-full w-full"
                style={{ objectPosition: who.image1_position || "center", transform: `scale(${who.image1_scale || 1})`, transformOrigin: who.image1_position || "center" }}
              />
            </div>
            <div className="rounded-sm overflow-hidden h-72 w-full mt-12">
              <img
                src={who.image2 || GROUPS_IMG}
                alt=""
                className="object-cover h-full w-full"
                style={{ objectPosition: who.image2_position || "center", transform: `scale(${who.image2_scale || 1})`, transformOrigin: who.image2_position || "center" }}
              />
            </div>
            <div className="col-span-2 rounded-sm overflow-hidden h-64 w-full">
              <img
                src={who.image3 || DUBAI_IMG}
                alt="Dubai"
                className="object-cover h-full w-full"
                style={{ objectPosition: who.image3_position || "center", transform: `scale(${who.image3_scale || 1})`, transformOrigin: who.image3_position || "center" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERMON PREVIEW */}
      {featuredSermon && (
        <section data-testid="sermons-preview-section" className="py-24 md:py-32 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Latest Word</p>
                <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter">From The Pulpit</h2>
              </div>
              <Link to="/sermons" data-testid="sermons-preview-link" className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#ff5c00] inline-flex items-center gap-2">
                All sermons <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 aspect-video rounded-sm overflow-hidden border border-white/10 bg-black">
                {featuredSermon.embed ? (
                  <iframe
                    data-testid="featured-sermon-iframe"
                    title={featuredSermon.title}
                    src={featuredSermon.embed}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500">
                    Video unavailable
                  </div>
                )}
              </div>
              <div className="lg:col-span-4 space-y-4">
                {featuredSermon.source === "youtube" && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-accent uppercase tracking-widest px-2 py-0.5 bg-red-600/20 text-red-300 rounded-sm">
                    Live from YouTube
                  </span>
                )}
                <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-zinc-500">{featuredSermon.date}</p>
                <h3 className="font-display text-3xl uppercase tracking-tight">{featuredSermon.title}</h3>
                {featuredSermon.speaker && <p className="text-zinc-400 text-sm">By {featuredSermon.speaker}</p>}
                {featuredSermon.description && (
                  <p className="text-zinc-300 line-clamp-4">{featuredSermon.description}</p>
                )}
                <a
                  href="https://youtube.com/@rccg_ugycdubai"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="featured-sermon-youtube-link"
                  className="inline-flex items-center gap-2 mt-4 border border-white/20 hover:border-[#ff5c00] hover:text-[#ff5c00] font-mono-accent text-xs tracking-[0.18em] uppercase px-5 py-3 rounded-sm transition-colors"
                >
                  <Play className="w-3 h-3" /> Subscribe on YouTube
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* EVENTS PREVIEW */}
      <section data-testid="events-preview-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Mark Your Calendar</p>
              <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter">Upcoming Events</h2>
            </div>
            <Link to="/events" data-testid="events-preview-link" className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#ff5c00] inline-flex items-center gap-2">
              See all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {events
              .filter((e) => !["Service", "Devotional", "Prayer"].includes(e.category))
              .slice(0, 4).map((ev, i) => (
              <Link
                to="/events"
                key={ev.id}
                data-testid={`event-card-${i}`}
                className="group relative overflow-hidden rounded-sm border border-white/10 bg-[#0f0f14] hover:border-[#ff5c00]/60 transition-all hover:-translate-y-1"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={ev.image_url || COMMUNITY_IMG}
                    alt={ev.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#ff5c00] mb-2">
                    {ev.category}
                  </p>
                  <h3 className="font-display text-lg uppercase tracking-tight leading-tight mb-2">{ev.title}</h3>
                  <div className="text-xs text-zinc-400 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {ev.date}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ev.time}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOUSE FELLOWSHIP CENTRES */}
      {fellowships.length > 0 && (
        <section data-testid="fellowships-section" className="py-20 md:py-28 px-6 md:px-12 bg-[#080808] border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl mb-12">
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">
                {fellowshipBlock.eyebrow || "Find Community Near You"}
              </p>
              <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter mb-4">
                {fellowshipBlock.heading || "House Fellowship Centres"}
              </h2>
              {fellowshipBlock.intro && (
                <p className="text-zinc-300 leading-relaxed">{fellowshipBlock.intro}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {fellowships.map((f, i) => (
                <motion.div
                  key={`${f.name}-${i}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                  data-testid={`fellowship-card-${i}`}
                  className={`bg-[#0f0f14] border rounded-sm p-6 hover:border-[#ff5c00]/40 transition-colors ${f.is_online ? "border-[#ff5c00]/30" : "border-white/10"}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-display text-xl uppercase tracking-tight">{f.name}</h3>
                      <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-zinc-500 mt-1">
                        {f.location}
                      </p>
                    </div>
                    {f.is_online && (
                      <span className="font-mono-accent text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm border border-[#ff5c00]/40 text-[#ff5c00]">
                        Online
                      </span>
                    )}
                  </div>
                  {(f.day || f.time) && (
                    <p className="text-zinc-300 text-sm mb-2">
                      <span className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">Meets · </span>
                      {[f.day, f.time].filter(Boolean).join(" at ")}
                    </p>
                  )}
                  {f.address && (
                    <p className="text-zinc-400 text-xs leading-relaxed mb-3">{f.address}</p>
                  )}
                  {(f.coordinator || f.phone) && (
                    <div className="pt-3 border-t border-white/5">
                      <p className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Coordinator</p>
                      {f.coordinator && <p className="text-zinc-200 text-sm">{f.coordinator}</p>}
                      {f.phone && (
                        <a href={`tel:${f.phone}`} data-testid={`fellowship-phone-${i}`} className="text-[#ff5c00] text-xs font-mono-accent hover:underline">
                          {f.phone}
                        </a>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MINISTRIES */}
      <section data-testid="ministries-preview-section" className="py-24 md:py-32 px-6 md:px-12">        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Find Your Tribe</p>
              <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter">Ministries</h2>
            </div>
            <Link to="/ministries" data-testid="ministries-preview-link" className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#ff5c00] inline-flex items-center gap-2">
              Explore all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {ministries.slice(0, 3).map((m, i) => (
              <Link
                key={m.id}
                to={`/ministries/${m.slug}`}
                data-testid={`ministry-card-${i}`}
                className="group relative overflow-hidden rounded-sm border border-white/10 aspect-[4/5]"
              >
                {m.hero_image ? (
                  <img src={m.hero_image} alt={m.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                ) : (
                  <div className="w-full h-full bg-[#0a0a0d]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <MinistryIcon name={m.icon} className="w-6 h-6 text-[#ff5c00] mb-3" />
                  <h3 className="font-display text-3xl uppercase tracking-tight">{m.name}</h3>
                  <p className="text-zinc-300 text-sm mt-2 max-w-xs line-clamp-3">{m.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIES */}
      {testimonies.length > 0 && (
        <section data-testid="testimonies-preview-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#080808] border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">All Glory to God</p>
                <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter">
                  Testimonies
                </h2>
              </div>
              <Link
                to="/testimonies"
                data-testid="testimonies-preview-link"
                className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#ff5c00] inline-flex items-center gap-2"
              >
                Read All <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {testimonies.slice(0, 2).map((t, i) => (
                <motion.article
                  key={t.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`testimony-card-${i}`}
                  className="relative bg-[#0f0f14] border border-white/10 rounded-sm p-7 md:p-9 hover:border-[#ff5c00]/40 transition-colors"
                >
                  <Sparkles className="absolute top-6 right-6 w-4 h-4 text-[#ff5c00]/40" />
                  <div className="flex items-center gap-4 mb-5">
                    {t.image_url ? (
                      <img
                        src={t.image_url}
                        alt={t.first_name}
                        className="w-14 h-14 rounded-sm object-cover border border-white/10"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-sm bg-[#ff5c00]/10 border border-[#ff5c00]/20 flex items-center justify-center">
                        <Flame className="w-5 h-5 text-[#ff5c00]" />
                      </div>
                    )}
                    <div>
                      <p className="font-display text-lg uppercase tracking-tight text-white">
                        {t.first_name}{t.last_initial ? ` ${t.last_initial}.` : ""}
                      </p>
                      <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#ff5c00] mt-0.5">
                        {t.category}
                      </p>
                    </div>
                  </div>
                  <p className="text-zinc-300 leading-relaxed line-clamp-6 whitespace-pre-wrap">
                    {t.testimony}
                  </p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FIRST-TIME VISITOR */}
      <FirstVisit />
    </div>
  );
}
